import {
  Box,
  Button,
  Flex,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import { MdImage, MdLightbulbOutline, MdOndemandVideo } from "react-icons/md";
import YouTube from "react-youtube";
import { studentPalette } from "../../../constants/studentPalette";
import { extractYouTubeVideoId } from "../../../utils/youtubeVideoId";
import { isValidQuestionAsset } from "./exam-answer-media";

const BASE_URL = "https://edutopiaonline.com/api";
const MODAL = {
  HINT: "hint",
  VIDEO: "video",
  CAMBRIDGE: "cambridge",
};

const ACADEMIC = {
  primary: studentPalette.primary,
  accent: studentPalette.secondary,
};

const youtubeOpts = {
  height: 360,
  width: 640,
  playerVars: {
    autoplay: 0,
    rel: 0,
  },
};

const QuestionResourceActions = ({
  question,
  variant = "review",
  examType,
  design = "default",
}) => {
  const [activeModal, setActiveModal] = useState(null);
  const isAcademic = design === "academic";
  const isSolve = variant === "solve";

  const hasHint = isValidQuestionAsset(question?.model_answer_image);
  const videoId = useMemo(() => extractYouTubeVideoId(question?.url), [question?.url]);
  const hasVideo = Boolean(videoId);
  const hasCambridge = isValidQuestionAsset(question?.cambridge_image);

  const showHint = isSolve ? Number(examType) === 2 && hasHint : hasHint;
  const showVideo = !isSolve && hasVideo;
  const showCambridge = !isSolve && hasCambridge;

  useEffect(() => {
    setActiveModal(null);
  }, [question?.id]);

  if (!showHint && !showVideo && !showCambridge) return null;

  const buttonSx = isAcademic
    ? {
        bg: ACADEMIC.primary,
        color: "white",
        px: { base: 5, md: 8 },
        py: 5,
        borderRadius: "xl",
        fontWeight: "800",
        textTransform: "uppercase",
        letterSpacing: "0.1em",
        fontSize: "xs",
        _hover: { bg: ACADEMIC.accent },
        boxShadow: "md",
      }
    : {
        bg: "main_1",
        color: "white",
        size: "sm",
      };

  const closeModal = () => setActiveModal(null);

  return (
    <>
      <Flex w="100%" justify="flex-end" gap={3} flexWrap="wrap" mb={4}>
        {showHint ? (
          <Button
            leftIcon={<MdLightbulbOutline size={18} />}
            onClick={() => setActiveModal(MODAL.HINT)}
            {...buttonSx}
          >
            Hint
          </Button>
        ) : null}
        {showVideo ? (
          <Button
            leftIcon={<MdOndemandVideo size={18} />}
            onClick={() => setActiveModal(MODAL.VIDEO)}
            {...buttonSx}
          >
            Video Hint
          </Button>
        ) : null}
        {showCambridge ? (
          <Button
            leftIcon={<MdImage size={18} />}
            onClick={() => setActiveModal(MODAL.CAMBRIDGE)}
            {...buttonSx}
          >
            Cambridge solution
          </Button>
        ) : null}
      </Flex>

      <Modal isCentered isOpen={activeModal === MODAL.HINT} onClose={closeModal} motionPreset="slideInBottom">
        <ModalOverlay />
        <ModalContent maxW={{ base: "calc(100vw - 24px)", md: "xl" }}>
          <ModalHeader fontSize="md" fontWeight="800">
            Hint
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6} display="flex" justifyContent="center">
            <Image
              src={`${BASE_URL}/${question.model_answer_image}`}
              alt="Hint"
              w="100%"
              maxW={{ base: "100%", md: "480px" }}
              maxH="400px"
              objectFit="contain"
              borderRadius="md"
            />
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal isCentered isOpen={activeModal === MODAL.VIDEO} onClose={closeModal} motionPreset="slideInBottom">
        <ModalOverlay />
        <ModalContent maxW={{ base: "calc(100vw - 24px)", md: "3xl" }}>
          <ModalHeader fontSize="md" fontWeight="800">
            Video Hint
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6} display="flex" justifyContent="center">
            <Box
              w="100%"
              maxW="640px"
              sx={{
                "& iframe": {
                  display: "block",
                  maxWidth: "100%",
                },
              }}
            >
              <YouTube
                videoId={videoId}
                opts={youtubeOpts}
                onReady={(event) => event.target.pauseVideo()}
              />
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal isCentered isOpen={activeModal === MODAL.CAMBRIDGE} onClose={closeModal} motionPreset="slideInBottom">
        <ModalOverlay />
        <ModalContent maxW={{ base: "calc(100vw - 24px)", md: "xl" }}>
          <ModalHeader fontSize="md" fontWeight="800">
            Cambridge solution
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6} display="flex" justifyContent="center">
            <Image
              src={`${BASE_URL}/${question.cambridge_image}`}
              alt="Cambridge solution"
              w="100%"
              maxW={{ base: "100%", md: "480px" }}
              maxH="400px"
              objectFit="contain"
              borderRadius="md"
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default QuestionResourceActions;
